import { useState } from 'react'
import { useForm } from 'react-hook-form'
import emailjs from '@emailjs/browser'
import { EMAILJS_CONFIG } from '../../utils/constants'

// MIGRATION POINT: Contact form submission
// Current: EmailJS third-party service
// Future: POST to /api/contact endpoint
// Remember to add CSRF protection and rate limiting

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  const onSubmit = async (data) => {
    // Check if EmailJS is configured
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.PUBLIC_KEY) {
      setSubmitStatus('error')
      console.error('EmailJS is not configured. Please set environment variables.')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          from_name: data.name,
          from_email: data.email,
          message: data.message,
          to_name: 'Gabriele Wiedemann',
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      )

      setSubmitStatus('success')
      reset() // Clear form
    } catch (error) {
      console.error('Email sending failed:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <h2 className="text-2xl font-serif text-primary mb-6">Nachricht senden</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            {...register('name', {
              required: 'Bitte geben Sie Ihren Namen ein',
              minLength: { value: 2, message: 'Name muss mindestens 2 Zeichen lang sein' },
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
              errors.name ? 'border-red-500' : 'border-neutral-300'
            }`}
            placeholder="Ihr Name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
            E-Mail <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
              },
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
              errors.email ? 'border-red-500' : 'border-neutral-300'
            }`}
            placeholder="ihre.email@beispiel.de"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">
            Nachricht <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            rows={6}
            {...register('message', {
              required: 'Bitte geben Sie eine Nachricht ein',
              minLength: { value: 10, message: 'Nachricht muss mindestens 10 Zeichen lang sein' },
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none ${
              errors.message ? 'border-red-500' : 'border-neutral-300'
            }`}
            placeholder="Ihre Nachricht an mich..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
          )}
        </div>

        {/* Privacy Checkbox */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="privacy"
            {...register('privacy', {
              required: 'Bitte akzeptieren Sie die Datenschutzerklärung',
            })}
            className="mt-1 h-4 w-4 text-primary focus:ring-primary border-neutral-300 rounded"
          />
          <label htmlFor="privacy" className="ml-3 text-sm text-neutral-600">
            Ich stimme der{' '}
            <a href="/datenschutz" className="text-primary hover:text-secondary underline" target="_blank" rel="noopener noreferrer">
              Datenschutzerklärung
            </a>{' '}
            zu <span className="text-red-500">*</span>
          </label>
        </div>
        {errors.privacy && (
          <p className="text-sm text-red-500">{errors.privacy.message}</p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full px-6 py-3 text-white font-medium rounded-lg transition-all duration-300 ${
            isSubmitting
              ? 'bg-neutral-400 cursor-not-allowed'
              : 'bg-primary hover:bg-secondary shadow-md hover:shadow-lg'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Wird gesendet...
            </span>
          ) : (
            'Nachricht senden'
          )}
        </button>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-800">
                <strong>Vielen Dank!</strong> Ihre Nachricht wurde erfolgreich gesendet. Ich melde mich bald bei Ihnen.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">
                <strong>Fehler!</strong> Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie mich direkt per E-Mail.
              </p>
            </div>
          </div>
        )}
      </form>

      {/* Info Text */}
      <p className="mt-6 text-xs text-neutral-500">
        * Pflichtfelder. Ihre Daten werden nur zur Bearbeitung Ihrer Anfrage verwendet und nicht an Dritte weitergegeben.
      </p>
    </div>
  )
}
